import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('gd_admin_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Auto-flatten i18n dict fields for public responses so pages receive strings.
// Admin endpoints keep raw {es,en,fr} objects for editing.
const I18N_LANG_KEYS = new Set(['es', 'en', 'fr']);

function isI18nDict(v) {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const keys = Object.keys(v);
  if (keys.length === 0) return false;
  return keys.every((k) => I18N_LANG_KEYS.has(k));
}

function flattenI18n(value, lang) {
  if (isI18nDict(value)) {
    return value[lang] || value.es || Object.values(value).find(Boolean) || '';
  }
  if (Array.isArray(value)) return value.map((v) => flattenI18n(v, lang));
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = flattenI18n(v, lang);
    return out;
  }
  return value;
}

api.interceptors.response.use((res) => {
  try {
    const url = res.config?.url || '';
    if (!url.includes('/admin/')) {
      const lang = localStorage.getItem('gd_lang') || 'es';
      res.data = flattenI18n(res.data, lang);
    }
  } catch (_) { /* noop */ }
  return res;
});

// Resolve relative upload URLs to absolute backend URLs
export const resolveImg = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

// Public
export const getExperiences = (status) => api.get('/experiences', { params: status ? { status } : {} }).then((r) => r.data);
export const getExperience = (id) => api.get(`/experiences/${id}`).then((r) => r.data);
export const getTeam = () => api.get('/team').then((r) => r.data);
export const getCollaborators = () => api.get('/collaborators').then((r) => r.data);
export const getOfferings = () => api.get('/offerings').then((r) => r.data);
export const getSettings = () => api.get('/settings').then((r) => r.data);

export const postContact = (data) => api.post('/contact', data).then((r) => r.data);
export const postInscription = (data) => api.post('/inscriptions', data).then((r) => r.data);
export const postNewsletter = (email) => api.post('/newsletter', { email }).then((r) => r.data);

// Admin
export const adminLogin = (email, password) => api.post('/admin/login', { email, password }).then((r) => r.data);
export const adminMe = () => api.get('/admin/me').then((r) => r.data);
export const adminStats = () => api.get('/admin/stats').then((r) => r.data);

export const uploadFile = (file, folder = 'general') => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('folder', folder);
  return api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
};

// Admin CRUD
export const adminCreateExperience = (d) => api.post('/admin/experiences', d).then((r) => r.data);
export const adminUpdateExperience = (id, d) => api.put(`/admin/experiences/${id}`, d).then((r) => r.data);
export const adminDeleteExperience = (id) => api.delete(`/admin/experiences/${id}`).then((r) => r.data);

export const adminCreateTeam = (d) => api.post('/admin/team', d).then((r) => r.data);
export const adminUpdateTeam = (id, d) => api.put(`/admin/team/${id}`, d).then((r) => r.data);
export const adminDeleteTeam = (id) => api.delete(`/admin/team/${id}`).then((r) => r.data);

export const adminCreateCollab = (d) => api.post('/admin/collaborators', d).then((r) => r.data);
export const adminUpdateCollab = (id, d) => api.put(`/admin/collaborators/${id}`, d).then((r) => r.data);
export const adminDeleteCollab = (id) => api.delete(`/admin/collaborators/${id}`).then((r) => r.data);

export const adminCreateOffering = (d) => api.post('/admin/offerings', d).then((r) => r.data);
export const adminUpdateOffering = (id, d) => api.put(`/admin/offerings/${id}`, d).then((r) => r.data);
export const adminDeleteOffering = (id) => api.delete(`/admin/offerings/${id}`).then((r) => r.data);

export const adminUpdateSettings = (d) => api.put('/admin/settings', d).then((r) => r.data);

export const adminGetContacts = () => api.get('/admin/contacts').then((r) => r.data);
export const adminMarkContactRead = (id) => api.put(`/admin/contacts/${id}/read`).then((r) => r.data);
export const adminDeleteContact = (id) => api.delete(`/admin/contacts/${id}`).then((r) => r.data);

export const adminGetInscriptions = () => api.get('/admin/inscriptions').then((r) => r.data);
export const adminMarkInsRead = (id) => api.put(`/admin/inscriptions/${id}/read`).then((r) => r.data);
export const adminDeleteIns = (id) => api.delete(`/admin/inscriptions/${id}`).then((r) => r.data);

export const adminGetNewsletter = () => api.get('/admin/newsletter').then((r) => r.data);
export const adminDeleteNewsletter = (id) => api.delete(`/admin/newsletter/${id}`).then((r) => r.data);

// News
export const getNews = (limit) => api.get('/news', { params: limit ? { limit } : {} }).then((r) => r.data);
export const getNewsItem = (slugOrId) => api.get(`/news/${slugOrId}`).then((r) => r.data);
export const adminListNews = () => api.get('/admin/news').then((r) => r.data);
export const adminCreateNews = (d) => api.post('/admin/news', d).then((r) => r.data);
export const adminUpdateNews = (id, d) => api.put(`/admin/news/${id}`, d).then((r) => r.data);
export const adminDeleteNews = (id) => api.delete(`/admin/news/${id}`).then((r) => r.data);
export const adminReorderNews = (ids) => api.put('/admin/news/reorder', { ids }).then((r) => r.data);

// Minisites
export const getMinisite = (slug) => api.get(`/minisites/${slug}`).then((r) => r.data);
export const adminListMinisites = () => api.get('/admin/minisites').then((r) => r.data);
export const adminGetMinisite = (id) => api.get(`/admin/minisites/${id}`).then((r) => r.data);
export const adminCreateMinisite = (d) => api.post('/admin/minisites', d).then((r) => r.data);
export const adminUpdateMinisite = (id, d) => api.put(`/admin/minisites/${id}`, d).then((r) => r.data);
export const adminDeleteMinisite = (id) => api.delete(`/admin/minisites/${id}`).then((r) => r.data);

export default api;
